import { Form, Formik, FormikHelpers } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';
import { ButtonGroup, passwordValidation, FormikPasswordWithButtonInput } from '@td-client-frontend/common';
import { FormikValidatePasswordInput } from '../components/Formik/FormikValidatePasswordInput';
import { toast } from 'react-toastify';
import { useAxios } from '../context/AxiosContext';
import { trackEvent } from '../api/piwik/piwikService';
import { Button } from '../components/form-controls/Button';
import { PiwikCategory, PiwikAction, PiwikName, Nullable, Errors } from '../@types/common';
import { useAuth } from '../context/AuthContext';
import { Tooltip } from '../components/Tooltip';

type FormValues = { newPassword: string; verifyNewPassword: string; currentPassword: string };

export function ChangePassword() {
  const { onPasswordChange } = useAuth();
  const { authAxios } = useAxios();
  const [tasErrors, setTasErrors] = useState<Nullable<string[]>>(null);

  const handleSubmit = async ({ currentPassword, newPassword, verifyNewPassword }: FormValues) => {
    trackEvent({
      category: PiwikCategory.BUTTONS,
      action: PiwikAction.CLICKED,
      name: PiwikName.CHANGE_PASSWORD
    });

    try {
      await authAxios.post('/change-password', {
        confirmPassword: verifyNewPassword,
        newPassword,
        password: currentPassword
      });

      toast.success('Your password has been changed', {
        position: 'top-center',
        hideProgressBar: true,
        pauseOnHover: true,
        draggable: true
      });

      onPasswordChange();
    } catch (err) {
      if (err?.response) {
        const {
          data: { errors = null }
        } = err.response;
        setTasErrors(errors);
        errors.forEach((error: any) =>
          toast.warning(error, {
            position: 'top-center',
            hideProgressBar: true,
            pauseOnHover: true,
            draggable: true
          })
        );
      } else {
        toast.warning('We were unable to update your password, please verify your information and try again', {
          position: 'top-center',
          hideProgressBar: true,
          pauseOnHover: true,
          draggable: true
        });
      }
    }
  };

  return (
    <div
      className="flex flex-col justify-start w-full h-full max-w-2xl mx-auto font-effra"
      data-testid="registerContainer"
    >
      <div className="flex items-center">
        <h2 className="my-6 mr-2 ml-6 text-4xl font-medium leading-tight font-effra">Change password</h2>
        <Tooltip
          type="restricted-words-in-password"
          text="Password cannot contain the words “Teladoc”, “Password”, or previously used password phrases."
        />
      </div>

      <Formik
        initialValues={
          {
            currentPassword: '',
            newPassword: '',
            verifyNewPassword: ''
          } as FormValues
        }
        validationSchema={Yup.object({
          currentPassword: Yup.string().required('Required'),
          newPassword: passwordValidation.when('currentPassword', {
            is: (exist: any) => exist,
            then: (schema: any) =>
              schema.notOneOf(
                [Yup.ref('currentPassword')],
                'You have used this password before, please choose a new one.'
              )
          }),
          verifyNewPassword: passwordValidation.when('newPassword', {
            is: (exist: any) => exist,
            then: (schema: any) => schema.oneOf([Yup.ref('newPassword')], 'Passwords must match.')
          })
        })}
        onSubmit={async (values, { setSubmitting }: FormikHelpers<FormValues>) => {
          setSubmitting(true);
          await handleSubmit(values);
          setSubmitting(false);
        }}
      >
        {({ isSubmitting, isValid, errors }) => (
          <Form data-testid="reset-pass-form" className="p-8 m-6 bg-white shadow-lg rounded-lg">
            {tasErrors && (
              <div className="mb-8 text-center text-red-500">
                {tasErrors.map((error) => (
                  <p key={error}>{error}</p>
                ))}
              </div>
            )}
            {(errors.newPassword || errors.verifyNewPassword) === Errors.InvalidPasswordEntered && (
              <div className="flex mb-4" data-testid="restricted-word-notifier">
                <p>&#128683;</p>
                <p className="w-2/3 ml-2">
                  Password cannot contain the words “Teladoc”, “Password”, or previously used password phrases. Please
                  choose a new password.
                </p>
              </div>
            )}

            <div className="max-w-sm">
              <FormikPasswordWithButtonInput
                label="Current Password"
                isRequired
                placeholder="Please enter current password"
                data-testid="current-password"
                name="currentPassword"
                autoComplete="current-password"
              />
              <FormikValidatePasswordInput
                label="New Password"
                isRequired
                placeholder="Please enter password"
                data-testid="new-password"
                name="newPassword"
                showFieldError
                autoComplete="new-password"
              />
              <FormikPasswordWithButtonInput
                label="Verify New Password"
                isRequired
                placeholder="Please enter password again"
                data-testid="verify-new-password"
                name="verifyNewPassword"
                showFieldError
                autoComplete="new-password"
              />
            </div>
            <div className="w-full max-w-sm">
              <ButtonGroup>
                <Button
                  data-testid="cancelButton"
                  disabled={isSubmitting}
                  kind="secondary"
                  onClick={onPasswordChange}
                  name="cancel"
                >
                  Cancel
                </Button>
                <Button data-testid="submitButton" disabled={isSubmitting || !isValid} kind="primary" type="submit">
                  Confirm
                </Button>
              </ButtonGroup>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default ChangePassword;
